import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';
import { useSectionContext } from '@/contexts/SectionContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Edit,
  Eye,
  Trash2,
  User,
  Calendar,
  MapPin,
  Activity,
  Hospital,
  Clock,
  UserCheck,
  ChevronRight,
  Stethoscope,
  Search,
  Filter,
  X,
  Maximize2,
  Minimize2,
  Baby,
  AlertTriangle,
  UserPlus,
  Building2,
  CheckCircle2,
  Heart
} from 'lucide-react';
import { formatDate } from '@/utils/formatters';
import { Patient, PatientFilters } from '@/types/patient.types';
import { cn } from '@/utils/cn';
import { TakeoverPatientDialog } from '@/components/patients/TakeoverPatientDialog';
import { Switch } from '@/components/ui/switch';
import { PrematuritateModal } from '@/components/prematuritate/PrematuritateModal';

// Search and filter interfaces
interface SearchFilters {
  searchQuery: string;
  dateFrom: string;
  dateTo: string;
  hospitalId: string;
  chdDiagnosis: string;
  patientStatus: string;
}


// Vizualizează Pacient Dialog Component
interface ViewPatientDialogProps {
  patient: Patient | null;
  isOpen: boolean;
  onClose: () => void;
}

const ViewPatientDialog: React.FC<ViewPatientDialogProps> = ({
  patient,
  isOpen,
  onClose,
}) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (isOpen) {
      setActiveTab('general');
    }
  }, [isOpen]);

  if (!patient) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        hideClose
        className={isMaximized ? "w-screen h-screen max-w-none max-h-none overflow-y-auto flex flex-col" : "max-w-4xl max-h-[90vh] overflow-y-auto"}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <User className="h-6 w-6" />
              Detalii pacient
            </DialogTitle>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsMaximized(!isMaximized)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                aria-label={isMaximized ? 'Micșorează fereastra' : 'Maximizează fereastra'}
              >
                {isMaximized ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </button>
              <DialogClose asChild>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label="Închide dialogul"
                >
                  <X className="h-4 w-4" />
                </button>
              </DialogClose>
            </div>
          </div>
          <DialogDescription>
            Vizualizare completă a informațiilor pacientului
          </DialogDescription>
        </DialogHeader>

        <div className={cn("mt-6 flex-1", isMaximized && "flex flex-col")}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className={cn("w-full", isMaximized && "flex-1 flex flex-col")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general">Date generale</TabsTrigger>
            <TabsTrigger value="history">Istoricul modificărilor</TabsTrigger>
          </TabsList>

            {/* Tab Date Generale */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserCheck className="h-5 w-5" />
                    Informații personale
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Cod pacient</label>
                      <p className="text-sm font-semibold">{patient.patient_code}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">CNP</label>
                      <p className="text-sm font-semibold">{patient.cnp || 'Nespecificat'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Nume complet</label>
                      <p className="text-sm font-semibold">{patient.name || 'Nespecificat'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Data nașterii</label>
                      <p className="text-sm font-semibold flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(patient.birth_date)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Sex</label>
                      <Badge variant={patient.gender === 0 ? 'secondary' : 'default'}>
                        {patient.gender === 0 ? 'Feminin' : 'Masculin'}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Greutate la naștere</label>
                      <p className="text-sm font-semibold">
                        {patient.birth_weight ? `${patient.birth_weight} g` : 'Nespecificat'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Vârsta gestațională</label>
                      <p className="text-sm font-semibold">
                        {patient.gestational_age ? `${patient.gestational_age} săptămâni` : 'Nespecificat'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Rezultat naștere</label>
                      <p className="text-sm font-semibold">
                        {patient.birth_outcome ? 
                          (patient.birth_outcome === 1 ? 'Născut viu' : 
                           patient.birth_outcome === 2 ? 'Născut mort' : 
                           patient.birth_outcome === 3 ? 'Întrerupere pentru anomalie' : 'Nespecificat')
                          : 'Nespecificat'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Status</label>
                      <Badge variant="outline" className="bg-green-50">
                        <Activity className="h-3 w-3 mr-1" />
                        Activ
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Informații Locație
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Județ Naștere</label>
                      <p className="text-sm">{patient.county || 'Nespecificat'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Localitate Naștere</label>
                      <p className="text-sm">{patient.locality || 'Nespecificat'}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Maternitate</label>
                    <p className="text-sm">{patient.maternity || 'Nespecificat'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">ID foaie observație (MRN)</label>
                    <p className="text-sm">{patient.external_id || 'Nespecificat'}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Hospital className="h-5 w-5" />
                    Spital & Înregistrare
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Spital</label>
                    <p className="text-sm font-semibold">{(patient.hospital_name || '').trim() || 'Nespecificat'}</p>
                    <p className="text-xs text-muted-foreground">ID #{patient.hospital_id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Data Înregistrării</label>
                    <p className="text-sm flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDate(patient.created_at)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Ultima Actualizare</label>
                    <p className="text-sm flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDate(patient.updated_at)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>


            {/* Tab Istoric */}
            <TabsContent value="history" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Istoricul modificărilor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b">
                      <div>
                        <p className="text-sm font-medium">Pacient creat</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(patient.created_at)}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-green-50">
                        <UserCheck className="h-3 w-3 mr-1" />
                        Creat
                      </Badge>
                    </div>
                    {patient.updated_at && patient.updated_at !== patient.created_at && (
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <p className="text-sm font-medium">Ultima actualizare</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(patient.updated_at)}
                          </p>
                        </div>
                        <Badge variant="outline" className="bg-blue-50">
                          <Edit className="h-3 w-3 mr-1" />
                          Actualizat
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="p-2"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            Închide
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface PatientListProps {
  patients: Patient[];
  isLoading: boolean;
  error: any;
  onEdit: (patient: Patient) => void;
  // Opens the MCC data dialog (heart icon); the icon is hidden when not provided.
  onEditMcc?: (patient: Patient) => void;
  onView?: (patient: Patient) => void;
  onDelete?: (patient: Patient) => void;
  pagination?: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
    onPageChange: (page: number) => void;
  };
  // New filter props
  onFiltersChange?: (filters: PatientFilters) => void;
  initialFilters?: PatientFilters;
  hospitals?: Array<{ id: number; name?: string; hospital_name?: string }>;
  userRole?: string;
  // External filter control
  isFiltersExpanded?: boolean;
  onFiltersToggle?: (expanded: boolean) => void;
}

export const PatientList: React.FC<PatientListProps> = ({
  patients,
  isLoading,
  error,
  onEdit,
  onEditMcc,
  onView,
  onDelete,
  pagination,
  onFiltersChange,
  initialFilters,
  hospitals = [],
  userRole = 'hospital',
  isFiltersExpanded,
  onFiltersToggle,
}) => {
  const { navigate } = useNavigation();
  const { activeSection } = useSectionContext();
  const isChdSection = activeSection === 'chd';
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [internalFiltersOpen, setInternalFiltersOpen] = useState(false);
  const [takeoverDialogOpen, setTakeoverDialogOpen] = useState(false);
  const [patientToTakeover, setPatientToTakeover] = useState<Patient | null>(null);
  const [prematuritateModalOpen, setPrematuritateModalOpen] = useState(false);
  const [prematuritatePatient, setPrematuritatePatient] = useState<Patient | null>(null);
  const [showOwnedOnly, setShowOwnedOnly] = useState(true);

  // Use external filter control if provided, otherwise use internal state
  const filtersOpen = isFiltersExpanded !== undefined ? isFiltersExpanded : internalFiltersOpen;
  const setFiltersOpen = onFiltersToggle || setInternalFiltersOpen;
  
  // Search and filter state
  const [filters, setFilters] = useState<SearchFilters>({
    searchQuery: initialFilters?.search || '',
    dateFrom: initialFilters?.birth_date_from || '',
    dateTo: initialFilters?.birth_date_to || '',
    hospitalId: initialFilters?.hospital_id?.toString() || '',
    chdDiagnosis: initialFilters?.severe_chd !== undefined ? (initialFilters.severe_chd ? 'yes' : 'no') : 'all',
    patientStatus: 'active', // Default to active since we don't have inactive patients in the system
  });

  const hospitalNameById = useMemo(() => {
    return hospitals.reduce<Record<number, string>>((acc, hospital) => {
      const name = (hospital.name || hospital.hospital_name || '').trim();
      if (name) {
        acc[hospital.id] = name;
      }
      return acc;
    }, {});
  }, [hospitals]);

  const getHospitalDisplayName = useCallback((patient: Patient): string => {
    const fromPatient = (patient.hospital_name || '').trim();
    if (fromPatient) {
      return fromPatient;
    }
    const lookupName = hospitalNameById[patient.hospital_id];
    if (lookupName) {
      return lookupName;
    }
    return 'Nespecificat';
  }, [hospitalNameById]);
  
  // Debounce timer for search
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Apply filters function
  const applyFilters = useCallback((newFilters: SearchFilters, ownedOverride?: boolean) => {
    const patientFilters: PatientFilters = {
      search: newFilters.searchQuery || undefined,
      birth_date_from: newFilters.dateFrom || undefined,
      birth_date_to: newFilters.dateTo || undefined,
      hospital_id: newFilters.hospitalId && newFilters.hospitalId !== 'all' ? parseInt(newFilters.hospitalId) : undefined,
      severe_chd: newFilters.chdDiagnosis === 'all' ? undefined : newFilters.chdDiagnosis === 'yes',
    };
    
    const shouldShowOwned = ownedOverride !== undefined ? ownedOverride : showOwnedOnly;
    if (shouldShowOwned) {
      patientFilters.owned = true;
    } else {
      patientFilters.owned = undefined;
    }

    onFiltersChange?.(patientFilters);
  }, [onFiltersChange, showOwnedOnly]);
  
  // Debounced search effect
  const debouncedSearch = useCallback((searchValue: string) => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    const timer = setTimeout(() => {
      applyFilters({ ...filters, searchQuery: searchValue });
    }, 300);
    
    setSearchDebounceTimer(timer);
  }, [filters, searchDebounceTimer, applyFilters]);
  
  // Handle search input change
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, searchQuery: value }));
    debouncedSearch(value);
  };
  
  // Handle filter changes
  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };
  
  // Get active filter count
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.hospitalId && filters.hospitalId !== 'all' && userRole === 'admin') count++;
    if (filters.chdDiagnosis !== 'all') count++;
    if (filters.patientStatus !== 'active') count++;
    if (showOwnedOnly) count++;
    return count;
  };
  
  // Clear all filters
  const clearFilters = () => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
      setSearchDebounceTimer(null);
    }
    const clearedFilters: SearchFilters = {
      searchQuery: '',
      dateFrom: '',
      dateTo: '',
      hospitalId: '',
      chdDiagnosis: 'all',
      patientStatus: 'active',
    };
    setFilters(clearedFilters);
    applyFilters(clearedFilters);
  };
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  useEffect(() => {
    applyFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOwnedOnly]);

  const handleViewPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setViewDialogOpen(true);
    // Also call the parent's onView if provided
    onView?.(patient);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedPatient(null);
  };

  // Handle takeover patient click
  const handleTakeoverClick = (patient: Patient) => {
    setPatientToTakeover(patient);
    setTakeoverDialogOpen(true);
  };

  // Handle successful takeover
  const handleTakeoverSuccess = (updatedPatient: Patient) => {
    setTakeoverDialogOpen(false);
    setPatientToTakeover(null);
    // Trigger a refresh by calling the filter change
    if (onFiltersChange) {
      onFiltersChange({ ...filters, owned: showOwnedOnly ? true : undefined });
    }
  };

  // Handle prematuritate modal
  const handlePrematuritateClick = (patient: Patient) => {
    setPrematuritatePatient(patient);
    setPrematuritateModalOpen(true);
  };

  const handlePrematuritateClose = () => {
    setPrematuritateModalOpen(false);
    setPrematuritatePatient(null);
  };

  // Handle owned filter toggle
  const handleOwnedFilterToggle = (checked: boolean) => {
    setShowOwnedOnly(checked);
    applyFilters({ ...filters }, checked);
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          A apărut o eroare la încărcarea pacienților. Vă rugăm încercați din nou.
        </AlertDescription>
      </Alert>
    );
  }

  const activeFilterCount = getActiveFilterCount();
  const hasActiveFilters = activeFilterCount > 0;
  
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lista pacienților</CardTitle>
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount} filtru{activeFilterCount !== 1 ? 'ri' : ''} activ{activeFilterCount !== 1 ? 'e' : ''}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Owned Patients Filter Toggle */}
          {userRole === 'hospital' && (
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
              <div className="flex items-center space-x-2">
                <Switch
                  id="owned-only"
                  checked={showOwnedOnly}
                  onCheckedChange={handleOwnedFilterToggle}
                />
                <Label htmlFor="owned-only" className="flex items-center gap-2 cursor-pointer">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>Doar pacienții mei</span>
                </Label>
              </div>
              {showOwnedOnly && (
                <Badge variant="secondary" className="ml-2">
                  Afișare pacienți proprii
                </Badge>
              )}
            </div>
          )}

          {/* Search and Filters Section */}
          <div className="space-y-4 mb-6">
            {/* Search Bar */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Căutați după nume pacient, cod pacient, nume mamă sau data nașterii..."
                  value={filters.searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 pr-4"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Șterge filtre
              </Button>
            </div>
            
            {/* Collapsible Filters */}
            <Accordion type="single" collapsible value={filtersOpen ? 'filters' : ''} onValueChange={(value) => setFiltersOpen(value === 'filters')}>
              <AccordionItem value="filters" className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    <span>Filtre avansate</span>
                    {hasActiveFilters && (
                      <Badge variant="secondary" className="ml-2">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Date Range Filters */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Data nașterii de la</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          type="date"
                          value={filters.dateFrom}
                          onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Data nașterii până la</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          type="date"
                          value={filters.dateTo}
                          onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    {/* Hospital Filter (Admin Only) */}
                    {userRole === 'admin' && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Spital</label>
                        <Select 
                          value={filters.hospitalId} 
                          onValueChange={(value) => handleFilterChange('hospitalId', value)}
                        >
                          <SelectTrigger>
                            <div className="flex items-center gap-2">
                              <Hospital className="h-4 w-4 text-muted-foreground" />
                              <SelectValue placeholder="Toate spitalele" />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Toate spitalele</SelectItem>
                            {hospitals.map((hospital) => (
                              <SelectItem key={hospital.id} value={hospital.id.toString()}>
                                {hospital.name || hospital.hospital_name || 'Necunoscut'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* MCC Diagnosis Filter */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Diagnostic MCC</label>
                      <Select 
                        value={filters.chdDiagnosis} 
                        onValueChange={(value) => handleFilterChange('chdDiagnosis', value)}
                      >
                        <SelectTrigger>
                          <div className="flex items-center gap-2">
                            <Stethoscope className="h-4 w-4 text-muted-foreground" />
                            <SelectValue placeholder="Toate" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toate</SelectItem>
                          <SelectItem value="yes">Da - MCC sever</SelectItem>
                          <SelectItem value="no">Nu - MCC non-sever</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Patient Status Filter */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Status pacient</label>
                      <Select 
                        value={filters.patientStatus} 
                        onValueChange={(value) => handleFilterChange('patientStatus', value)}
                      >
                        <SelectTrigger>
                          <div className="flex items-center gap-2">
                            <Activity className="h-4 w-4 text-muted-foreground" />
                            <SelectValue placeholder="Toate" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toate</SelectItem>
                          <SelectItem value="active">Activ</SelectItem>
                          <SelectItem value="inactive">Inactiv</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          
          {/* Table */}
          {patients.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-12 text-center">
              <User className="mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? 'Nu există rezultate' : 'Nu există pacienți'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {hasActiveFilters
                  ? 'Ajustați criteriile de căutare sau ștergeți filtrele pentru a vedea mai multe rezultate.'
                  : 'Începeți prin a adăuga primul pacient în sistem.'}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Acțiuni rapide</TableHead>
                    <TableHead>Cod</TableHead>
                    <TableHead>CNP</TableHead>
                    <TableHead>Nume</TableHead>
                    <TableHead>Data nașterii</TableHead>
                    <TableHead>Sex</TableHead>
                    <TableHead>Spital</TableHead>
                    {userRole === 'hospital' && <TableHead>Proprietate</TableHead>}
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow key={patient.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleViewPatient(patient)}
                            title="Detalii pacient"
                            className="h-9 w-9 hover:bg-blue-50"
                          >
                            <Eye className="h-5 w-5 text-blue-600" />
                          </Button>
                          {/* Only for patients the viewer actually owns - VonDataService's
                              validatePatientAccess() blocks any other hospital's patients,
                              so showing it otherwise just leads to a permission-error toast
                              over an empty modal. In the MCC section, also only show it for
                              patients that already have premature data - MCC users cannot
                              introduce new data, and offering the button for empty patients
                              would invite that. */}
                          {patient.is_owned && (!isChdSection || patient.has_premature_data) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handlePrematuritateClick(patient)}
                              title="Date prematuritate"
                              className="h-9 w-9 hover:bg-purple-50"
                            >
                              <Baby className="h-5 w-5 text-purple-600" />
                            </Button>
                          )}
                          {/* MCC registry data (medical, maternal, prenatal, diagnoses,
                              procedures, outcomes) - the MCC counterpart of the prematuritate
                              shortcut. Only for MCC patients the viewer owns. */}
                          {isChdSection && onEditMcc && patient.is_owned && !patient.is_premature && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => onEditMcc(patient)}
                              title="Date MCC"
                              className="h-9 w-9 hover:bg-red-50"
                            >
                              <Heart className="h-5 w-5 text-red-600" />
                            </Button>
                          )}
                          {patient.is_owned && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => onEdit(patient)}
                              title="Editează"
                              className="h-9 w-9 hover:bg-gray-100"
                            >
                              <Edit className="h-5 w-5 text-gray-700" />
                            </Button>
                          )}
                          {userRole === 'hospital' && !patient.is_owned && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleTakeoverClick(patient)}
                              title="Preia"
                              className="h-9 w-9 hover:bg-blue-50"
                            >
                              <UserPlus className="h-5 w-5 text-blue-600" />
                            </Button>
                          )}
                          {userRole === 'admin' && patient.is_owned && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => onDelete?.(patient)}
                              title="Șterge"
                              className="h-9 w-9 hover:bg-red-50"
                            >
                              <Trash2 className="h-5 w-5 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => handleViewPatient(patient)}
                          className="text-left transition-colors hover:text-primary focus:outline-none focus:underline"
                        >
                          {patient.patient_code}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <button
                          type="button"
                          onClick={() => handleViewPatient(patient)}
                          className="w-full text-left transition-colors hover:text-primary focus:outline-none focus:underline"
                        >
                          {patient.cnp || '-'}
                        </button>
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => handleViewPatient(patient)}
                          className="text-left transition-colors hover:text-primary focus:outline-none focus:underline"
                        >
                          {patient.name}
                        </button>
                      </TableCell>
                      <TableCell>{formatDate(patient.birth_date)}</TableCell>
                      <TableCell>
                        <Badge variant={patient.gender === 0 ? 'secondary' : 'default'}>
                          {patient.gender === 0 ? 'Feminin' : 'Masculin'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getHospitalDisplayName(patient)}</TableCell>
                      {userRole === 'hospital' && (
                        <TableCell>
                          {patient.is_owned ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                              Propriu
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                              <Building2 className="mr-1 h-3 w-3" />
                              Extern
                            </Badge>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant="outline" className="bg-green-50">
                          Activ
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Afișare {((pagination.currentPage - 1) * pagination.pageSize) + 1} - {' '}
                {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} din {' '}
                {pagination.totalItems} pacienți
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  Anterior
                </Button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <Button
                      key={page}
                      variant={page === pagination.currentPage ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => pagination.onPageChange(page)}
                    >
                      {page}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  Următor
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Patient Dialog */}
      <ViewPatientDialog
        patient={selectedPatient}
        isOpen={viewDialogOpen}
        onClose={handleCloseViewDialog}
      />

      {/* Takeover Patient Dialog */}
      <TakeoverPatientDialog
        isOpen={takeoverDialogOpen}
        patient={patientToTakeover}
        onClose={() => {
          setTakeoverDialogOpen(false);
          setPatientToTakeover(null);
        }}
        onSuccess={handleTakeoverSuccess}
      />

      {/* Prematuritate Modal */}
      {prematuritatePatient && (
        <PrematuritateModal
          open={prematuritateModalOpen}
          onOpenChange={handlePrematuritateClose}
          patient={prematuritatePatient}
          readOnly={isChdSection}
          onDataUpdated={() => {
            // Optionally refresh the patient list or data
          }}
        />
      )}
    </>
  );
};

// Export the ViewPatientDialog separately if needed
export { ViewPatientDialog };
